import React, { useEffect, useMemo, useState } from "react";
import { db, auth, storage } from "../../firebase/config";
import { 
  collection, 
  addDoc, 
  doc,
  getDoc,
  serverTimestamp,
  query,
  updateDoc,
  where,
  onSnapshot,
} from "firebase/firestore";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { requireVerifiedEmail } from "../../utils/emailVerification";
import {
  MARKETPLACE_SECTIONS,
  PLANT_CATEGORY_OPTIONS,
  PLANT_INVENTORY_CATEGORY_MAP,
  PLANT_LIFE_CYCLE_OPTIONS,
  PLANT_SUN_OPTIONS,
  PLANT_UNIT_OPTIONS,
  getPlantCategoryLabel,
  getPlantLifeCycleLabel,
  getSunNeedsLabel,
} from "../../constants/plantsGarden";
import {
  PRODUCT_AVAILABILITY,
  PRODUCT_AVAILABILITY_OPTIONS,
} from "../../constants/availability";
import {
  PRODUCT_PRICE_TYPES,
  PRODUCT_PRICE_TYPE_OPTIONS,
  formatAvailability,
  formatPriceUnit,
  formatProductPrice,
} from "../../utils/productDisplay";
import { getProfilePath, getStorefrontSlug, isStorefrontEnabled } from "../../utils/storefront";
import {
  completeTrackedFlow,
  startTrackedFlow,
  trackUsageEvent,
} from "../Analytics/CampaignTrafficTracker";
import BarterExchangeSection from "../Common/BarterExchangeSection";
import {
  DEFAULT_BARTER_FIELDS,
  buildBarterPayload,
  getExchangeBadges,
  normalizeBarterFields,
} from "../../utils/barter";
import {
  LOCATION_SOURCES,
  buildCanonicalLocationPayload,
  normalizeLocationRecord,
  normalizeProfileLocation,
  selectProductListingLocation,
} from "../../utils/locationUtils";
import {
  buildProductStandLinkPayload,
  getEligibleProductStands,
  getProductStandLinkId,
  shouldClearLegacyStandLocationSnapshot,
} from "../../utils/productStandLink";
import {
  abandonAppFlow,
  completeAppFlow,
  startAppFlow,
} from "../../utils/appAnalytics";

const inventoryCategoryToProductType = {
  feed: "other",
  hay: "hay_straw",
  seed: "plants",
  medicine: "other",
  supplies: "other",
  product: "produce",
  other: "other",
};

const PRODUCT_IMAGE_MAX_SIZE = 5 * 1024 * 1024;
const ALLOWED_PRODUCT_IMAGE_TYPES = ["image/jpeg", "image/png", "image/webp"];

const sanitizeProductImageFileName = (fileName = "product-image") => {
  const safeName = String(fileName)
    .trim()
    .replace(/\s+/g, "-")
    .replace(/[^a-zA-Z0-9._-]/g, "-")
    .replace(/-+/g, "-")
    .replace(/^\.+/, "")
    .slice(0, 120);

  return safeName || "product-image";
};

const AddProduct = ({
  userProfile,
  defaultMarketplaceSection = MARKETPLACE_SECTIONS.GENERAL,
}) => {
  const [name, setName] = useState("");
  const [price, setPrice] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [description, setDescription] = useState("");
  const [image, setImage] = useState(null);
  const [existingImageUrl, setExistingImageUrl] = useState("");
  const [uploading, setUploading] = useState(false);
  const [loadingListing, setLoadingListing] = useState(false);
  const [farmStands, setFarmStands] = useState([]);
  const [selectedStandId, setSelectedStandId] = useState("");
  const [pickupAvailable, setPickupAvailable] = useState(false);
  const [showOnLiveShelf, setShowOnLiveShelf] = useState(false);
  const [existingProductForEdit, setExistingProductForEdit] = useState(null);
  const [productType, setProductType] = useState("produce");
const [unit, setUnit] = useState("each");
const [priceType, setPriceType] = useState("per_unit");

const [hayType, setHayType] = useState("grass");
const [baleType, setBaleType] = useState("square");
const [cutting, setCutting] = useState("");
const [baleWeight, setBaleWeight] = useState("");
const [storedInside, setStoredInside] = useState(true);
const [deliveryAvailable, setDeliveryAvailable] = useState(false);
const [deliveryRadiusMiles, setDeliveryRadiusMiles] = useState(25);
const [plantCategory, setPlantCategory] = useState("vegetable_starts");
const [variety, setVariety] = useState("");
const [potSize, setPotSize] = useState("");
const [plantSize, setPlantSize] = useState("");
const [annualOrPerennial, setAnnualOrPerennial] = useState("");
const [sunNeeds, setSunNeeds] = useState("");
const [availabilityStart, setAvailabilityStart] = useState("");
const [availabilityEnd, setAvailabilityEnd] = useState("");
const [availabilityStatus, setAvailabilityStatus] = useState(PRODUCT_AVAILABILITY.AVAILABLE_NOW);
const [availabilityNote, setAvailabilityNote] = useState("");
const [pickupLocation, setPickupLocation] = useState("");
  const [plantDeliveryAvailable, setPlantDeliveryAvailable] = useState(false);
  const [existingListingLocation, setExistingListingLocation] = useState(null);
  const [barterFields, setBarterFields] = useState(DEFAULT_BARTER_FIELDS);
  const navigate = useNavigate();
  const routeLocation = useLocation();
  const [searchParams] = useSearchParams();
  const user = auth.currentUser;
  const editProductId = searchParams.get("edit");
  const isEditing = Boolean(editProductId);
  const inventoryDraft = routeLocation.state?.inventoryDraft || null;
  const linkedInventoryId = routeLocation.state?.linkedInventoryId || inventoryDraft?.id || "";
  const marketplaceSection =
    routeLocation.state?.marketplaceSection || defaultMarketplaceSection;
  const isPlantsGardenListing =
    marketplaceSection === MARKETPLACE_SECTIONS.PLANTS_GARDEN;

  const previewUrl = useMemo(() => {
    return image ? URL.createObjectURL(image) : null;
  }, [image]);

  useEffect(() => {
    if (!user?.uid) return;
    startTrackedFlow("add_product", {
      feature: isPlantsGardenListing ? "plants_garden" : "marketplace",
      action: isEditing ? "edit_product" : "add_product",
    });
    startAppFlow("add_product", "add_product_started", {
      feature: isPlantsGardenListing ? "plants_garden" : "marketplace",
      action: isEditing ? "edit_product" : "add_product",
      abandonEventType: "add_product_abandoned",
    });

    return () => abandonAppFlow("add_product");
  }, [isEditing, isPlantsGardenListing, user?.uid]);

  useEffect(() => {
  if (!user?.uid) return;

  const q = query(
    collection(db, "farmStands"),
    where("ownerId", "==", user.uid)
  );

  const unsub = onSnapshot(q, (snapshot) => {
    const stands = snapshot.docs
      .map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));

    setFarmStands(getEligibleProductStands(stands, user.uid));
  });

  return () => unsub();
}, [user?.uid]);

  useEffect(() => {
    const loadProductForEdit = async () => {
      if (!editProductId || !user?.uid) return;

      try {
        setLoadingListing(true);

        const productSnap = await getDoc(doc(db, "products", editProductId));

        if (!productSnap.exists()) {
          alert("Listing not found.");
          navigate("/plants-garden");
          return;
        }

        const product = { id: productSnap.id, ...productSnap.data() };
        setExistingProductForEdit(product);
        setExistingListingLocation(normalizeLocationRecord(product));
        const sellerId = product.userId || product.ownerId || product.sellerId;

        if (sellerId !== user.uid) {
          alert("You can only edit your own listing.");
          navigate("/plants-garden");
          return;
        }

        setName(product.name || product.plantName || "");
        setPrice(String(product.price ?? ""));
        setQuantity(Number(product.quantityAvailable ?? product.quantity ?? 1));
        setDescription(product.description || "");
        setExistingImageUrl(product.image || (Array.isArray(product.photos) ? product.photos[0] : "") || "");
        setProductType(product.productType || (product.marketplaceSection === MARKETPLACE_SECTIONS.PLANTS_GARDEN ? "plants" : "produce"));
        setUnit(product.unit || "each");
        setPriceType(
          product.priceStartsAt === true
            ? PRODUCT_PRICE_TYPES.STARTS_AT
            : product.priceType || PRODUCT_PRICE_TYPES.PER_UNIT
        );
        setHayType(product.hayDetails?.hayType || "grass");
        setBaleType(product.hayDetails?.baleType || "square");
        setCutting(product.hayDetails?.cutting || "");
        setBaleWeight(String(product.hayDetails?.baleWeight || "").replace(/\s*lb$/i, ""));
        setStoredInside(product.hayDetails?.storedInside !== false);
        setDeliveryAvailable(Boolean(product.hayDetails?.deliveryAvailable || product.deliveryAvailable));
        setDeliveryRadiusMiles(Number(product.hayDetails?.deliveryRadiusMiles || 25));
        setPlantCategory(product.plantCategory || "vegetable_starts");
        setVariety(product.variety || "");
        setPotSize(product.potSize || "");
        setPlantSize(product.plantSize || "");
        setAnnualOrPerennial(product.annualOrPerennial || "");
        setSunNeeds(product.sunNeeds || "");
        setAvailabilityStart(product.availabilityStart || "");
        setAvailabilityEnd(product.availabilityEnd || "");
        setAvailabilityStatus(product.availabilityStatus || PRODUCT_AVAILABILITY.AVAILABLE_NOW);
        setAvailabilityNote(product.availabilityNote || "");
        setPickupLocation(product.pickupLocation || "");
        setPlantDeliveryAvailable(Boolean(product.deliveryAvailable));
        const linkedStandId = getProductStandLinkId(product);
        setPickupAvailable(Boolean(product.pickupAvailable || linkedStandId));
        setSelectedStandId(linkedStandId);
        setShowOnLiveShelf(Boolean(linkedStandId && product.showOnLiveShelf !== false));
        setBarterFields(normalizeBarterFields(product));
      } catch (error) {
        console.error("Error loading product for edit:", error);
        alert("Could not load listing for editing.");
        navigate("/plants-garden");
      } finally {
        setLoadingListing(false);
      }
    };

    loadProductForEdit();
  }, [editProductId, user?.uid, navigate]);

  useEffect(() => {
    if (!inventoryDraft) return;

    setName(inventoryDraft.itemName || "");
    setQuantity(Math.max(1, Number(inventoryDraft.quantity || 1)));
    setDescription(inventoryDraft.notes || "");
    setAvailabilityStatus(PRODUCT_AVAILABILITY.AVAILABLE_NOW);
    setProductType(
      isPlantsGardenListing
        ? "plants"
        : inventoryCategoryToProductType[inventoryDraft.category] || "other"
    );
    setUnit(
      inventoryDraft.unit ||
        (isPlantsGardenListing
          ? "each"
          : inventoryDraft.category === "hay"
          ? "bale"
          : "each")
    );
    if (isPlantsGardenListing) {
      setPlantCategory(
        PLANT_INVENTORY_CATEGORY_MAP[inventoryDraft.category] || "other"
      );
    }

    const draftPrice = inventoryDraft.salePrice || inventoryDraft.price || inventoryDraft.costPerUnit || "";
    if (draftPrice !== "") setPrice(String(draftPrice));

    if (inventoryDraft.category === "hay") {
      setHayType("grass");
      setBaleType(inventoryDraft.unit === "roll" ? "round" : "square");
    }
  }, [inventoryDraft, isPlantsGardenListing]);

  const cleanQuantity = Math.max(1, Number(quantity || 1));
  const cleanPrice = Number(price || 0);
  const selectedStand = farmStands.find((stand) => stand.id === selectedStandId);
  
  const productTypeOptions = [
  { value: "produce", label: "🥕 Produce" },
  { value: "eggs", label: "🥚 Eggs" },
  { value: "meat", label: "🥩 Meat" },
  { value: "hay_straw", label: "🌾 Hay / Straw" },
  { value: "honey", label: "🍯 Honey" },
  { value: "plants", label: "🌱 Plants" },
  { value: "handmade", label: "🧼 Handmade" },
  { value: "other", label: "📦 Other" },
];

const unitOptions = [
  "each",
  "dozen",
  "lb",
  "bushel",
  "bale",
  "roll",
  "bag",
  "jar",
  "bundle",
  "case",
];

const handmadeUnitOptions = [
  "item",
  "piece",
  "set",
  "pair",
  "order",
  "bundle",
  ...unitOptions.filter((option) => option !== "bundle"),
];

const getSellerListingLocation = (profile = {}) =>
  normalizeProfileLocation(profile, { allowFallback: false });

const cleanBaleWeight = String(baleWeight || "").trim();

const formattedBaleWeight = cleanBaleWeight
  ? cleanBaleWeight.toLowerCase().includes("lb")
    ? cleanBaleWeight
    : `${cleanBaleWeight} lb`
  : "";

const isHayOrStraw = productType === "hay_straw";
  const activeUnitOptions = isPlantsGardenListing
    ? PLANT_UNIT_OPTIONS
    : productType === "handmade"
      ? handmadeUnitOptions
      : unitOptions;

  const handleProductImageChange = (event) => {
    const file = event.target.files?.[0] || null;

    if (!file) {
      setImage(null);
      return;
    }

    if (!ALLOWED_PRODUCT_IMAGE_TYPES.includes(file.type)) {
      alert("Please choose a JPEG, PNG, or WebP image.");
      event.target.value = "";
      setImage(null);
      return;
    }

    if (file.size > PRODUCT_IMAGE_MAX_SIZE) {
      alert("Please choose an image under 5 MB.");
      event.target.value = "";
      setImage(null);
      return;
    }

    setImage(file);
  };
  
  const handleAddProduct = async () => {
    if (!user) return alert("Not logged in");
    if (!requireVerifiedEmail(user, userProfile)) return;

    if (!name.trim() || !price || (!image && !existingImageUrl)) {
      return alert("Product name, price, and image are required.");
    }

    if (image && !ALLOWED_PRODUCT_IMAGE_TYPES.includes(image.type)) {
      return alert("Please choose a JPEG, PNG, or WebP image.");
    }

    if (image && image.size > PRODUCT_IMAGE_MAX_SIZE) {
      return alert("Please choose an image under 5 MB.");
    }

    if (isPlantsGardenListing && !plantCategory) {
      return alert("Please choose a plant category.");
    }

    if (cleanPrice <= 0) {
      return alert("Please enter a valid price.");
    }

    if (cleanQuantity <= 0) {
      return alert("Please enter a valid quantity.");
    }

    const confirmed = window.confirm(
      isEditing
        ? "Save changes to this listing?"
        : linkedInventoryId
        ? "Create this marketplace listing from the inventory draft? You can edit product stock here, but inventory will not be reduced automatically until you confirm a completed sale and choose to reduce inventory."
        : "Create this marketplace listing now?"
    );

    if (!confirmed) return;

    try {
      setUploading(true);

      let imageUrl = existingImageUrl;

      if (image) {
        const safeFileName = sanitizeProductImageFileName(image.name);
        const imageRef = ref(storage, `products/${user.uid}/${Date.now()}_${safeFileName}`);

        await uploadBytes(imageRef, image, { contentType: image.type });
        imageUrl = await getDownloadURL(imageRef);
      }

      const nextStandId = pickupAvailable && selectedStand ? selectedStand.id : "";
      const shouldClearLegacyStandLocation = shouldClearLegacyStandLocationSnapshot({
        existingProduct: existingProductForEdit,
        nextStandId,
      });
      const sellerListingLocation = nextStandId
        ? null
        : getSellerListingLocation(userProfile);
      const listingLocation = selectProductListingLocation({
        farmStandLocation: null,
        sellerProfile: sellerListingLocation,
        existingListing: shouldClearLegacyStandLocation ? null : existingListingLocation,
      });
      const locationPayload = buildCanonicalLocationPayload({
        ...listingLocation,
        locationSource: listingLocation?.locationSource || LOCATION_SOURCES.MISSING,
      });

      const productPayload = {
        name: name.trim(),
        sellerName: String(
          userProfile?.farmName ||
            userProfile?.businessName ||
            userProfile?.displayName ||
            userProfile?.name ||
            "Local seller"
        ).trim().slice(0, 120),
        sellerFarmName: String(userProfile?.farmName || "").trim().slice(0, 120),
        sellerBusinessName: String(userProfile?.businessName || "").trim().slice(0, 120),
        sellerPublicSlug: isStorefrontEnabled(userProfile || {})
          ? getStorefrontSlug(userProfile || {})
          : "",
        marketplaceSection,
        price: cleanPrice,
        priceType,
        priceStartsAt: priceType === PRODUCT_PRICE_TYPES.STARTS_AT,
        productType: isPlantsGardenListing ? "plants" : productType,
        unit,
        quantity: cleanQuantity,
        quantityAvailable: cleanQuantity,
        description: description.trim(),
        image: imageUrl,
        photos: [imageUrl],
        availabilityStatus,
        availabilityNote:
          availabilityStatus === PRODUCT_AVAILABILITY.COMING_SOON
            ? availabilityNote.trim()
            : "",

        plantName: isPlantsGardenListing ? name.trim() : "",
        variety: isPlantsGardenListing ? variety.trim() : "",
        plantCategory: isPlantsGardenListing ? plantCategory : "",
        potSize: isPlantsGardenListing ? potSize.trim() : "",
        plantSize: isPlantsGardenListing ? plantSize.trim() : "",
        annualOrPerennial: isPlantsGardenListing ? annualOrPerennial : "",
        sunNeeds: isPlantsGardenListing ? sunNeeds : "",
        availabilityStart:
          availabilityStatus === PRODUCT_AVAILABILITY.COMING_SOON || isPlantsGardenListing
            ? availabilityStart
            : "",
        availabilityEnd: isPlantsGardenListing ? availabilityEnd : "",
        pickupLocation: isPlantsGardenListing ? pickupLocation.trim() : "",
        deliveryAvailable: isPlantsGardenListing
          ? plantDeliveryAvailable
          : deliveryAvailable,
        ...buildBarterPayload(barterFields),

        hayDetails: isHayOrStraw
  ? {
      hayType,
      baleType,
      cutting: cutting.trim(),
      baleWeight: formattedBaleWeight,
      storedInside,
      deliveryAvailable,
      deliveryRadiusMiles: deliveryAvailable
        ? Number(deliveryRadiusMiles || 0)
        : 0,
    }
  : null,

        ...buildProductStandLinkPayload({
          selectedStand: pickupAvailable ? selectedStand : null,
          showOnLiveShelf,
        }),
        ...locationPayload,
        status:
          availabilityStatus === PRODUCT_AVAILABILITY.OUT_OF_STOCK
            ? "out_of_stock"
            : "active",
        isArchived: false,
        linkedInventoryId: linkedInventoryId || "",
        inventorySyncEnabled: false,
        updatedAt: serverTimestamp(),
      };

      let productRef;

      if (isEditing) {
        productRef = doc(db, "products", editProductId);
        await updateDoc(productRef, productPayload);
      } else {
        productRef = await addDoc(collection(db, "products"), {
          ...productPayload,
          userId: user.uid,
          ownerId: user.uid,
          createdAt: serverTimestamp(),
        });
      }

      if (!isEditing && linkedInventoryId) {
        await updateDoc(doc(db, "farmInventory", linkedInventoryId), {
          linkedProductId: productRef.id,
          ...(isPlantsGardenListing ? { linkedPlantListingId: productRef.id } : {}),
          inventorySyncEnabled: false,
          updatedAt: serverTimestamp(),
        });
      }

      completeTrackedFlow("add_product");
      completeAppFlow("add_product", "add_product_completed", {
        feature: isPlantsGardenListing ? "plants_garden" : "marketplace",
        action: isEditing ? "update" : "create",
        relatedId: productRef.id,
      });
      trackUsageEvent(isEditing ? "product_updated" : "product_created", {
        feature: isPlantsGardenListing ? "plants_garden" : "marketplace",
        action: isEditing ? "update" : "create",
        relatedId: productRef.id,
      });

      alert(
        isEditing
          ? "Listing updated!"
          : isPlantsGardenListing
          ? "Plant listing added!"
          : "Product added!"
      );
      navigate(
        isPlantsGardenListing
          ? "/plants-garden"
          : getProfilePath({ id: user.uid, uid: user.uid, ...(userProfile || {}) })
      );
    } catch (err) {
      console.error(err);
      alert(isEditing ? "Error updating listing" : "Error adding product");
    } finally {
      setUploading(false);
    }
  };

  if (loadingListing) {
    return (
      <div className="add-product-page page-shell page-shell-narrow">
        <div className="card">Loading listing...</div>
      </div>
    );
  }

  return (
    <div className="add-product-page page-shell page-shell-narrow">
      <div className="add-product-hero">
        <div>
          <span className="edit-profile-kicker">🛒 Farm Market</span>
          <h2>
            {isEditing
              ? isPlantsGardenListing
                ? "Edit Plant Listing"
                : "Edit Product"
              : isPlantsGardenListing
              ? "Add Plant Listing"
              : "Add Product"}
          </h2>
          <p>
            {isPlantsGardenListing
              ? "List starts, plants, seeds, bulbs, trees, shrubs, or garden supplies for local buyers."
              : "Create a clean product listing with a photo, price, quantity, and short description."}
          </p>
        </div>
      </div>

      <div className="add-product-layout add-product-clean-layout">
        <div className="edit-section-card">
          <div className="edit-section-heading">
            <h3>{isPlantsGardenListing ? "Plant Details" : "Product Details"}</h3>
            <p>
              {isPlantsGardenListing
                ? "Share the public details buyers need. Private inventory notes stay private."
                : "Tell buyers what you’re selling and how many are available."}
            </p>
          </div>

          <div className="context-tip">
            <strong>Tip:</strong> A clear name, one good photo, price, and pickup note
            are enough to publish your first listing. You can edit details later.
          </div>

          <div className="form-stack">
            <div>
              <label className="form-label">
                {isPlantsGardenListing ? "Plant name" : "Product Name"}
              </label>
              <input
                placeholder={
                  isPlantsGardenListing
                    ? "Example: Roma Tomato Starts"
                    : "Example: Goat Milk Soap"
                }
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </div>

             <div className="product-form-row">
  {isPlantsGardenListing ? (
    <div>
      <label className="form-label">Plant Category</label>
      <select
        value={plantCategory}
        onChange={(e) => setPlantCategory(e.target.value)}
      >
        {PLANT_CATEGORY_OPTIONS.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  ) : (
    <div>
      <label className="form-label">Product Type</label>
      <select
        value={productType}
        onChange={(e) => {
          const nextType = e.target.value;
          setProductType(nextType);

          if (nextType === "hay_straw") {
            setUnit("bale");
          } else if (nextType === "eggs") {
            setUnit("dozen");
          } else if (nextType === "honey") {
            setUnit("jar");
          } else if (nextType === "meat") {
            setUnit("lb");
          } else if (nextType === "handmade") {
            setUnit("item");
          } else {
            setUnit("each");
          }
        }}
      >
        {productTypeOptions.map((option) => (
          <option key={option.value} value={option.value}>
            {option.label}
          </option>
        ))}
      </select>
    </div>
  )}

  <div>
    <label className="form-label">Sell By</label>
    <select value={unit} onChange={(e) => setUnit(e.target.value)}>
      {activeUnitOptions.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  </div>
</div>


            {isPlantsGardenListing && (
              <div className="plant-details-box">
                <div className="product-form-row">
                  <div>
                    <label className="form-label">Variety optional</label>
                    <input
                      value={variety}
                      onChange={(e) => setVariety(e.target.value)}
                      placeholder="Example: Cherokee Purple"
                    />
                  </div>

                  <div>
                    <label className="form-label">Pot size optional</label>
                    <input
                      value={potSize}
                      onChange={(e) => setPotSize(e.target.value)}
                      placeholder="Example: 4 inch pot"
                    />
                  </div>
                </div>

                <div className="product-form-row">
                  <div>
                    <label className="form-label">Plant size optional</label>
                    <input
                      value={plantSize}
                      onChange={(e) => setPlantSize(e.target.value)}
                      placeholder="Example: 8-12 inches tall"
                    />
                  </div>

                  <div>
                    <label className="form-label">Annual or perennial</label>
                    <select
                      value={annualOrPerennial}
                      onChange={(e) => setAnnualOrPerennial(e.target.value)}
                    >
                      {PLANT_LIFE_CYCLE_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="product-form-row">
                  <div>
                    <label className="form-label">Sun needs</label>
                    <select value={sunNeeds} onChange={(e) => setSunNeeds(e.target.value)}>
                      {PLANT_SUN_OPTIONS.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="form-label">Pickup location</label>
                    <input
                      value={pickupLocation}
                      onChange={(e) => setPickupLocation(e.target.value)}
                      placeholder="Example: Shelbyville, TN"
                    />
                  </div>
                </div>

                <div className="product-form-row">
                  <div>
                    <label className="form-label">Available from optional</label>
                    <input
                      type="date"
                      value={availabilityStart}
                      onChange={(e) => setAvailabilityStart(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="form-label">Available until optional</label>
                    <input
                      type="date"
                      value={availabilityEnd}
                      onChange={(e) => setAvailabilityEnd(e.target.value)}
                    />
                  </div>
                </div>

                <label className="farm-stand-toggle">
                  <input
                    type="checkbox"
                    checked={plantDeliveryAvailable}
                    onChange={(e) => setPlantDeliveryAvailable(e.target.checked)}
                  />
                  Delivery available
                </label>
              </div>
            )}

            <div className="product-form-row">
              <div>
                <label className="form-label">
                  Price {unit ? `($ / ${formatPriceUnit(unit)})` : "($)"}
                 </label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Example: 15.00"
                  value={price}
                  onChange={(e) => setPrice(e.target.value)}
                />
              </div>

              <div>
                <label className="form-label">Pricing display</label>
                <select
                  value={priceType}
                  onChange={(e) => setPriceType(e.target.value)}
                >
                  {PRODUCT_PRICE_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                {priceType === PRODUCT_PRICE_TYPES.STARTS_AT && (
                  <small className="form-helper-text">
                    Best for custom orders where this is the starting price.
                  </small>
                )}
              </div>

              <div>
                <label className="form-label">Quantity Available</label>
                <input
                  type="number"
                  min="1"
                  step="1"
                  placeholder="Example: 12"
                  value={quantity}
                  onChange={(e) => setQuantity(e.target.value)}
                />
              </div>
            </div>

            <div className="availability-form-panel">
              <label className="form-label">
                When will this item be available?
                <select
                  value={availabilityStatus}
                  onChange={(e) => setAvailabilityStatus(e.target.value)}
                >
                  {PRODUCT_AVAILABILITY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </label>

              {availabilityStatus === PRODUCT_AVAILABILITY.COMING_SOON && (
                <div className="product-form-row">
                  <div>
                    <label className="form-label">Expected date optional</label>
                    <input
                      type="date"
                      value={availabilityStart}
                      onChange={(e) => setAvailabilityStart(e.target.value)}
                    />
                  </div>

                  <div>
                    <label className="form-label">Availability note optional</label>
                    <input
                      value={availabilityNote}
                      onChange={(e) => setAvailabilityNote(e.target.value)}
                      maxLength={160}
                      placeholder="Example: Ready after the next harvest."
                    />
                  </div>
                </div>
              )}
            </div>

            {isHayOrStraw && !isPlantsGardenListing && (
  <div className="hay-details-box">
    <div className="edit-section-heading compact">
      <h3>🌾 Hay / Straw Details</h3>
      <p>
        Help buyers find exactly what they need without the “know somebody who knows somebody” maze.
      </p>
    </div>

    <div className="product-form-row">
      <div>
        <label className="form-label">Forage Type</label>
        <select value={hayType} onChange={(e) => setHayType(e.target.value)}>
          <option value="grass">Grass Hay</option>
          <option value="alfalfa">Alfalfa</option>
          <option value="mixed">Mixed Hay</option>
          <option value="bermuda">Bermuda</option>
          <option value="orchard">Orchard Grass</option>
          <option value="timothy">Timothy</option>
          <option value="clover">Clover</option>
          <option value="straw">Straw</option>
          <option value="other">Other</option>
        </select>
      </div>

      <div>
        <label className="form-label">Bale Type</label>
        <select value={baleType} onChange={(e) => setBaleType(e.target.value)}>
          <option value="square">Square Bale</option>
          <option value="round">Round Bale</option>
          <option value="mini_round">Mini Round</option>
          <option value="large_square">Large Square</option>
          <option value="bundle">Bundle</option>
        </select>
      </div>
    </div>

    <div className="product-form-row">
      <div>
  <label className="form-label">Cutting</label>
  <select
    value={cutting}
    onChange={(e) => setCutting(e.target.value)}
  >
    <option value="">Not sure / not listed</option>
    <option value="1st cutting">1st Cutting</option>
    <option value="2nd cutting">2nd Cutting</option>
    <option value="3rd cutting">3rd Cutting</option>
    <option value="4th cutting">4th Cutting</option>
    <option value="mixed cutting">Mixed Cutting</option>
    <option value="new season">New Season Hay</option>
    <option value="last season">Last Season Hay</option>
  </select>
<small className="field-help-text">
  Choose the closest option. Leave blank if you are not sure.
</small>

</div>

      <div>
  <label className="form-label">Approx. Bale Weight</label>
  <input
    type="number"
    min="1"
    value={baleWeight}
    onChange={(e) => setBaleWeight(e.target.value)}
    placeholder="Example: 45"
  />
  <small className="field-help-text">
    We’ll display this as pounds, for example: 45 lb.
  </small>
</div>
    </div>

    <div className="hay-toggle-row">
      <label>
        <input
          type="checkbox"
          checked={storedInside}
          onChange={(e) => setStoredInside(e.target.checked)}
        />
        Stored inside / under cover
      </label>

      <label>
        <input
          type="checkbox"
          checked={deliveryAvailable}
          onChange={(e) => setDeliveryAvailable(e.target.checked)}
        />
        Delivery available
      </label>
    </div>

    {deliveryAvailable && (
  <div className="hay-delivery-radius-box">
    <label className="form-label">
      Delivery Radius: {deliveryRadiusMiles} miles
    </label>

    <input
      type="range"
      min="5"
      max="100"
      step="5"
      value={deliveryRadiusMiles}
      onChange={(e) => setDeliveryRadiusMiles(Number(e.target.value))}
    />

    <p>
      Delivery available within <strong>{deliveryRadiusMiles} miles</strong>.
    </p>
  </div>
)}
  </div>
)}

            <BarterExchangeSection
              value={barterFields}
              onChange={setBarterFields}
              title="Barter / trade options"
            />

            {!isPlantsGardenListing && (
            <div className="farm-stand-select-box">
  <div className="farm-stand-toggle">
    <strong>Where is this product available?</strong>

    <label>
      <input
        type="radio"
        name="product-availability-location"
        checked={!pickupAvailable}
        onChange={() => {
          setPickupAvailable(false);
          setSelectedStandId("");
          setShowOnLiveShelf(false);
        }}
      />
      Arrange pickup / contact me directly
    </label>

    <label>
      <input
        type="radio"
        name="product-availability-location"
        checked={pickupAvailable}
        onChange={() => {
          setPickupAvailable(true);
          if (selectedStandId) setShowOnLiveShelf(true);
        }}
      />
      Use one of my farm stands / pickup spots
    </label>
  </div>

  {pickupAvailable && (
    <>
      {farmStands.length === 0 ? (
        <div className="farm-stand-empty">
          <p>You do not have an active public stand / pickup spot saved yet.</p>

          <button
            type="button"
            className="btn-soft"
            onClick={() => navigate("/add-farm-stand")}
          >
            🥕 Add Stand / Pickup Spot
          </button>
        </div>
      ) : (
        <div>
          <label className="form-label">Choose Stand / Pickup Spot</label>

          <select
            value={selectedStandId}
            onChange={(e) => {
              const nextStandId = e.target.value;
              setSelectedStandId(nextStandId);
              setShowOnLiveShelf(Boolean(nextStandId));
            }}
          >
            <option value="">Select a stand / pickup spot</option>

            {farmStands.map((stand) => (
              <option key={stand.id} value={stand.id}>
                {stand.standName}{" "}
                {stand.city || stand.state
                  ? `- ${[stand.city, stand.state].filter(Boolean).join(", ")}`
                  : ""}
              </option>
            ))}
          </select>

          {selectedStand && (
            <div className="farm-stand-preview-mini">
              <strong>{selectedStand.standName}</strong>

              <p>
                📍{" "}
                {selectedStand.locationText ||
                  [selectedStand.city, selectedStand.state]
                    .filter(Boolean)
                    .join(", ") ||
                  "Location not listed"}
              </p>

              {selectedStand.hoursText && <p>🕒 {selectedStand.hoursText}</p>}

              {Array.isArray(selectedStand.paymentMethods) &&
                selectedStand.paymentMethods.length > 0 && (
                  <p>
                    💵{" "}
                    {selectedStand.paymentMethods
                      .map((method) => method.charAt(0).toUpperCase() + method.slice(1))
                      .join(", ")}
                  </p>
                )}
            </div>
          )}

          {selectedStand && (
            <label className="farm-stand-toggle">
              <input
                type="checkbox"
                checked={showOnLiveShelf}
                onChange={(e) => setShowOnLiveShelf(e.target.checked)}
              />
              Show this product on this stand&apos;s Live Shelf
            </label>
          )}
        </div>
      )}
    </>
  )}
</div>
            )}



            <div>
              <label className="form-label">Description</label>
              <textarea
                placeholder={
                  isPlantsGardenListing
                    ? "Public notes buyers should see: growing condition, age, pickup details, care notes..."
                    : "Tell buyers what makes this product special..."
                }
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                rows={5}
              />
            </div>

            <div>
              <label className="file-drop-card">
                <span>
                  {isEditing && existingImageUrl
                    ? "Replace Listing Image"
                    : isPlantsGardenListing
                    ? "Add Plant Image"
                    : "📸 Add Product Image"}
                </span>
                <small>A clear photo helps your listing sell faster.</small>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  onChange={handleProductImageChange}
                />
              </label>
            </div>
          </div>
        </div>

        <div className="edit-preview-panel">
          <div className="product-preview-card add-product-preview-card">
            <div className="summary-title">Live Preview</div>

            {previewUrl || existingImageUrl ? (
              <img
                src={previewUrl || existingImageUrl}
                className="preview-img"
                alt="Preview"
              />
            ) : (
              <div className="product-img product-img-placeholder">
                Product image preview
              </div>
            )}

            <div className="product-preview-body">
              <h3>{name || "Your Product Name"}</h3>

              <p className="price">
  {formatProductPrice({
    price: price ? cleanPrice : 0,
    unit,
    priceType,
    priceStartsAt: priceType === PRODUCT_PRICE_TYPES.STARTS_AT,
  })}
</p>

              <span className="acre-badge">
                Available: {formatAvailability(cleanQuantity, unit)}
              </span>

              <span className="acre-badge">
                {isPlantsGardenListing
                  ? getPlantCategoryLabel(plantCategory)
                  : productTypeOptions.find((option) => option.value === productType)?.label}
              </span>

              <div className="exchange-badge-row">
                {getExchangeBadges(buildBarterPayload(barterFields)).map((badge) => (
                  <span className="exchange-badge" key={badge}>
                    {badge}
                  </span>
                ))}
              </div>

              {isPlantsGardenListing && (
                <div className="plant-preview-mini">
                  {variety && <p>Variety: {variety}</p>}
                  {potSize && <p>Pot size: {potSize}</p>}
                  {plantSize && <p>Plant size: {plantSize}</p>}
                  {annualOrPerennial && <p>{getPlantLifeCycleLabel(annualOrPerennial)}</p>}
                  {sunNeeds && <p>Sun: {getSunNeedsLabel(sunNeeds)}</p>}
                  {pickupLocation && <p>Pickup: {pickupLocation}</p>}
                  {plantDeliveryAvailable && <p>Delivery available</p>}
                </div>
              )}

              {isHayOrStraw && !isPlantsGardenListing && (
  <div className="hay-preview-mini">
    <p>
      🌾 {hayType.replace("_", " ")} • {baleType.replace("_", " ")}
    </p>

    {cutting && <p>Cutting: {cutting}</p>}
    {formattedBaleWeight && <p>Approx. weight: {formattedBaleWeight}</p>}
    <p>{storedInside ? "Stored inside" : "Stored outside"}</p>
    {deliveryAvailable && (
  <p>Delivery available within {deliveryRadiusMiles} miles</p>
)}
  </div>
)}

              <p className="desc">
                {description || "Your product description will appear here."}
              </p>
            </div>
          </div>
        </div>
      </div>

      <div className="button-group add-product-actions">
        <button
          type="button"
          className="btn-soft"
          onClick={() =>
            navigate(
              isPlantsGardenListing
                ? "/plants-garden"
                : getProfilePath({ id: user?.uid, uid: user?.uid, ...(userProfile || {}) })
            )
          }
        >
          Cancel
        </button>

        <button
          type="button"
          className="btn-primary"
          onClick={handleAddProduct}
          disabled={uploading}
        >
          {uploading
            ? "Saving..."
            : isEditing
            ? "Update Listing"
            : isPlantsGardenListing
            ? "Add Plant Listing"
            : "Add Product"}
        </button>
      </div>
    </div>
  );
};

export default AddProduct;
